import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  Box,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import React from "react";
import {
  useDeleteServiceMutation,
  useLazyGetServicesListQuery,
  useToggleServiceMutation,
} from "../../service/services";
import { showError } from "../../constants/toasts";
import { ServiceDetails } from "../../types/General";
import { PRICE_TYPE } from "../../utils/enums";
import { formatDuration } from "../../utils/commonFunctions";
import Loader from "../../constants/Loader";
import CustomPagination from "../../components/CustomPagination";
import WarnModal from "../../Modals/warnmodal";
import { showToast } from "../../constants/toasts";
import { isValidInput } from "../../utils/validations";

const ServiceListing = () => {
  const navigate = useNavigate();
  const [getServicesList] = useLazyGetServicesListQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [serviceStatus, setServiceStatus] = useState<{ [key: string]: string }>(
    {},
  );
  const [services, setServices] = useState<ServiceDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteServiceMethod] = useDeleteServiceMutation();
  const [toggleService] = useToggleServiceMutation();

  const handleDelete = async (
    deleteById: any,
    selectedId: string,
    getAllData: () => void,
  ) => {
    if (selectedId) {
      try {
        const response = await deleteById({ id: selectedId }).unwrap();
        if (response?.statusCode === 200) {
          showToast(`Deleted Successfully`);
          if (getAllData) {
            getAllData();
          }
        } else {
          showError("Failed to delete");
        }
      } catch (error: any) {
        const errorMsg = error?.data?.message || "";
        if (errorMsg.toLowerCase().includes("booking")) {
          showError("This service cannot be deleted because it has one or more active bookings. Please complete or cancel all ongoing bookings before deleting the service.");
        } else {
          showError(errorMsg || "Failed to delete service.");
        }
      }
    }
  };

  const handleStatusChange = async (
    event: SelectChangeEvent,
    serviceId: string,
  ) => {
    const newStatus = event.target.value;
    const isActive = newStatus === "Active";

    try {
      const response = await toggleService({
        id: serviceId,
        body: { isActive },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast("Status Updated Successfully");
        getServicesListApi();
      } else {
        showError("Failed to update status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    }
  };

  const getServicesListApi = async () => {
    try {
      setLoading(true);
      const response = await getServicesList({
        page: page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setServices(response?.data?.list || []);
        setCount(response?.data?.total || 0);
      } else {
        setServices([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getServicesListApi();
  }, [page, debouncedSearchTerm]);

  return (
    <>
      <Loader isLoad={loading} />
      <div className="main_title ">
        <h1>Service Listings Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          Manage Services
        </p>
      </div>
      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => isValidInput(e.target.value) && setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search by service name..."
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              <AddIcon /> Add Service
            </Button>
          </div>
        </div>

        <TableContainer className="table_container" sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Service Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sub-category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Service Status</TableCell>
                <TableCell>Number of Bookings</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.length > 0 ? (
                services?.map((service, index) => (
                  <TableRow key={service?.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{service?.name || "-"}</TableCell>
                    <TableCell>{service?.category?.name || "-"}</TableCell>
                    <TableCell>{service?.subCategory?.name || "-"}</TableCell>
                    <TableCell>
                      {service?.price || "-"}{" "}
                      {Number(service?.priceType) === PRICE_TYPE.HOURLY
                        ? "hourly"
                        : Number(service?.priceType) === PRICE_TYPE.FIXED
                          ? "fixed"
                          : "package"}
                    </TableCell>
                    <TableCell>{formatDuration(service?.slotTime)}</TableCell>
                    <TableCell>
                      <div className="form">
                        <Select
                          value={service?.isActive ? "Active" : "Inactive"}
                          onChange={(e) => handleStatusChange(e, service?.id)}
                          size="small"
                          fullWidth
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                      </div>
                    </TableCell>
                    <TableCell>{service?.totalBookings || "-"}</TableCell>
                    <TableCell>
                      {" "}
                      <div className="table_actions v2">
                        <Box>
                          <IconButton
                            onClick={() =>
                              navigate(`/service-listing/view/${service?.id}`)
                            }
                          >
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              navigate(`/service-listing/edit/${service?.id}`)
                            }
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="View"
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setSelectedId(service?.id || "");
                              setOpen(true);
                            }}
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="View"
                            />
                          </IconButton>
                        </Box>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No services found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {services?.length ? (
          <CustomPagination
            currentPage={page - 1}
            totalPages={Math.ceil(count / 10)}
            count={count}
            pageSize={10}
            onPageChange={(page) => setPage(page + 1)}
          />
        ) : null}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() =>
          handleDelete(deleteServiceMethod, selectedId, getServicesListApi)
        }
        name="service"
      />
    </>
  );
};

export default ServiceListing;
